import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { getLeadPriceCalendar } from "../../services/api";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

interface Fare {
    departureDate: string;
    lowestFare: number;
    currencyCode: string;
}

interface Props {
    origin: string;
    destination: string;
    selectedDate: string;
    onDateChange?: (newDate: string) => void | Promise<void>;
    loading?: boolean;
}

const TILE_MIN_WIDTH = 96;     // desired minimum width per tile
const TILE_GAP = 6;            // gap-1.5 in Tailwind

function fmtYMD(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function addDays(ymd: string, n: number): string {
    const d = new Date(ymd + "T00:00:00");
    d.setDate(d.getDate() + n);
    return fmtYMD(d);
}
function monthKey(ymd: string): string {
    return ymd.slice(0, 7); // "YYYY-MM"
}
function fmtShort(ymd: string): { day: string; date: string; dow: string } {
    const d = new Date(ymd + "T00:00:00");
    return {
        dow: d.toLocaleDateString("en-US", { weekday: "short" }),
        date: String(d.getDate()),
        day: d.toLocaleDateString("en-US", { month: "short" }),
    };
}
function todayYMD(): string {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return fmtYMD(d);
}

const DatePriceStrip: React.FC<Props> = ({
    origin,
    destination,
    selectedDate,
    onDateChange,
    loading: externalLoading,
}) => {
    const [fares, setFares] = useState<Map<string, Fare>>(new Map());
    const [loading, setLoading] = useState(false);
    const [fetchAttempted, setFetchAttempted] = useState(false);
    const [anchorDate, setAnchorDate] = useState(selectedDate);

    // Responsive tile count based on container width.
    const railRef = useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = useState(7);

    useEffect(() => {
        setAnchorDate(selectedDate);
    }, [selectedDate]);

    useLayoutEffect(() => {
        const el = railRef.current;
        if (!el) return;
        const measure = () => {
            const w = el.clientWidth;
            if (w <= 0) return;
            // Solve: n * TILE_MIN_WIDTH + (n-1) * TILE_GAP <= w  →  n = (w + GAP) / (MIN + GAP)
            const n = Math.max(3, Math.floor((w + TILE_GAP) / (TILE_MIN_WIDTH + TILE_GAP)));
            // Force odd so selectedDate sits centered.
            const odd = n % 2 === 0 ? n - 1 : n;
            setVisibleCount(Math.max(3, odd));
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        window.addEventListener("resize", measure);
        return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
    }, []);

    // Build visible date list centered around anchor.
    const visibleDates = useMemo(() => {
        const half = Math.floor(visibleCount / 2);
        const today = todayYMD();
        const out: string[] = [];
        for (let i = -half; i <= half; i++) {
            out.push(addDays(anchorDate, i));
        }
        // If the earliest date is in the past, slide the whole window forward
        // so only future dates are shown.
        if (out[0] < today) {
            const shiftBy = Math.max(0, daysBetween(out[0], today));
            return out.map((d) => addDays(d, shiftBy));
        }
        return out;
    }, [anchorDate, visibleCount]);

    // Fetch every distinct month that the visible window touches.
    useEffect(() => {
        if (!origin || !destination || visibleDates.length === 0) return;
        let cancelled = false;

        const months = Array.from(new Set(visibleDates.map(monthKey))).sort();
        // Haven't fetched yet: include months not currently covered in `fares`.
        const coveredMonths = new Set<string>();
        Array.from(fares.keys()).forEach((k) => coveredMonths.add(monthKey(k)));
        const toFetch = months.filter((m) => !coveredMonths.has(m));
        if (toFetch.length === 0) return;

        setLoading(true);
        Promise.all(
            toFetch.map((m) =>
                getLeadPriceCalendar({
                    origin,
                    destination,
                    departureDate: m + "-01",
                })
                    .then((r) => {
                        console.log(`[DatePriceStrip] ${origin}->${destination} ${m}:`, r);
                        return r;
                    })
                    .catch((e) => {
                        console.error(`[DatePriceStrip] ${origin}->${destination} ${m} failed:`, e);
                        return null;
                    }),
            ),
        ).then((results) => {
            if (cancelled) return;
            setFetchAttempted(true);
            setFares((prev) => {
                const next = new Map(prev);
                for (const res of results) {
                    if (!res || !(res as any).success || !Array.isArray((res as any).fares)) continue;
                    for (const f of (res as any).fares) {
                        const key = (f.departureDate || "").slice(0, 10);
                        if (key) next.set(key, f);
                    }
                }
                return next;
            });
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });

        return () => { cancelled = true; };
        // eslint-disable-next-line
    }, [origin, destination, visibleDates.join("|")]);

    const cheapestDate = useMemo(() => {
        let best: Fare | null = null;
        for (const f of Array.from(fares.values())) {
            if (!best || f.lowestFare < best.lowestFare) best = f;
        }
        return best?.departureDate?.slice(0, 10) ?? null;
    }, [fares]);

    const cheapestIsVisible = cheapestDate ? visibleDates.includes(cheapestDate) : false;
    const cheapestOffLeft = !!(cheapestDate && !cheapestIsVisible && cheapestDate < visibleDates[0]);
    const cheapestOffRight = !!(cheapestDate && !cheapestIsVisible && cheapestDate > visibleDates[visibleDates.length - 1]);

    const handlePick = (date: string) => {
        if (date === selectedDate) return;
        onDateChange?.(date);
    };

    const shift = (n: number) => setAnchorDate(addDays(anchorDate, n));
    const isBusy = loading || externalLoading;

    const earliestDisabled = visibleDates[0] <= todayYMD();
    const showEmptyBanner = fetchAttempted && !loading && fares.size === 0;

    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {showEmptyBanner && (
                <div className="px-4 py-1.5 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-700 text-center">
                    Day-by-day fare preview is not available for this route right now.
                </div>
            )}
            <div className="flex items-stretch gap-1 px-2 py-2">
                <button
                    onClick={() => shift(-Math.max(1, Math.floor(visibleCount / 2)))}
                    disabled={earliestDisabled}
                    className="w-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 shrink-0 disabled:opacity-30 disabled:cursor-not-allowed relative"
                    aria-label="Earlier dates"
                >
                    <FaChevronLeft size={11} />
                    {cheapestOffLeft && (
                        <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-emerald-500 rounded-full" title={`Cheapest fare on ${cheapestDate}`} />
                    )}
                </button>

                <div ref={railRef} className="flex-1 flex gap-1.5 min-w-0">
                    {visibleDates.map((date) => {
                        const f = fares.get(date);
                        const isSelected = date === selectedDate;
                        const isCheapest = date === cheapestDate && !!f;
                        const parts = fmtShort(date);
                        return (
                            <button
                                key={date}
                                onClick={() => handlePick(date)}
                                className={`flex-1 min-w-0 px-2 py-2 rounded-lg border text-center transition ${
                                    isSelected
                                        ? "bg-primary border-primary text-white shadow-sm"
                                        : isCheapest
                                        ? "bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                                        : "bg-white border-gray-200 text-gray-700 hover:border-primary hover:bg-blue-50"
                                }`}
                            >
                                <div className={`text-[10px] font-semibold uppercase tracking-wider truncate ${isSelected ? "text-white/80" : "text-gray-400"}`}>
                                    {parts.dow}
                                </div>
                                <div className={`text-sm font-bold mt-0.5 truncate ${isSelected ? "text-white" : ""}`}>
                                    {parts.date} {parts.day}
                                </div>
                                <div className={`text-xs mt-0.5 font-medium truncate ${isSelected ? "text-white/90" : isCheapest ? "text-emerald-700" : "text-gray-500"}`}>
                                    {isBusy && !f ? "…" : f ? `${f.currencyCode} ${Math.round(f.lowestFare).toLocaleString()}` : "—"}
                                </div>
                                {isCheapest && !isSelected && (
                                    <div className="text-[9px] font-bold text-emerald-600 mt-0.5">Cheapest</div>
                                )}
                            </button>
                        );
                    })}
                </div>

                <button
                    onClick={() => shift(Math.max(1, Math.floor(visibleCount / 2)))}
                    className="w-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 shrink-0 relative"
                    aria-label="Later dates"
                >
                    <FaChevronRight size={11} />
                    {cheapestOffRight && (
                        <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-emerald-500 rounded-full" title={`Cheapest fare on ${cheapestDate}`} />
                    )}
                </button>
            </div>
        </div>
    );
};

function daysBetween(a: string, b: string): number {
    const da = new Date(a + "T00:00:00").getTime();
    const db = new Date(b + "T00:00:00").getTime();
    return Math.round((db - da) / 86400000);
}

export default DatePriceStrip;
